import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc, serverTimestamp, writeBatch, arrayUnion } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';

export const isOverdue = (task) => {
  if (!task.dueDate || task.status === 'completed' || task.status === 'cancelled') return false;
  const due = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
  return due < new Date();
};

const isAssignedTo = (task, employeeId) =>
  task.assigneeId === employeeId ||
  (task.assignees && task.assignees.some(a => a.id === employeeId));

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

/**
 * Tasks of the signed-in employee for "Görevlerim". Loads whenever the modal
 * opens and marks the employee's unseen tasks as viewed (the ids stay in
 * `unseenTaskIds` so they can still be highlighted this time).
 */
const useMyTasks = (isOpen) => {
  const { employeeId } = useUserProfile();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('active');
  const [unseenTaskIds, setUnseenTaskIds] = useState(() => new Set());

  const fetchTasks = useCallback(async () => {
    try {
      setIsLoading(true);
      const tasksRef = collection(db, 'Tasks');
      const q = query(tasksRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const tasksList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setTasks(tasksList);

      // Mark unseen tasks assigned to me as viewed
      if (employeeId) {
        const unseenMine = tasksList.filter(task => {
          if (!isAssignedTo(task, employeeId)) return false;
          return !(Array.isArray(task.viewedBy) && task.viewedBy.includes(employeeId));
        });
        setUnseenTaskIds(new Set(unseenMine.map(t => t.id)));
        if (unseenMine.length > 0) {
          const batch = writeBatch(db);
          unseenMine.forEach(t => {
            batch.update(doc(db, 'Tasks', t.id), { viewedBy: arrayUnion(employeeId) });
          });
          await batch.commit();
        }
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setIsLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (isOpen) fetchTasks();
  }, [isOpen, fetchTasks]);

  // All users (including admins) only see their own tasks
  const mine = useMemo(() => tasks.filter(task => isAssignedTo(task, employeeId)), [tasks, employeeId]);

  const myTasks = useMemo(() => {
    let filtered = mine;
    if (statusFilter === 'active') {
      filtered = filtered.filter(t => t.status === 'pending' || t.status === 'in-progress');
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    // Sort: overdue first, then by priority (high > medium > low)
    return [...filtered].sort((a, b) => {
      const aOverdue = isOverdue(a) ? 0 : 1;
      const bOverdue = isOverdue(b) ? 0 : 1;
      if (aOverdue !== bOverdue) return aOverdue - bOverdue;
      return (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3);
    });
  }, [mine, statusFilter]);

  const stats = useMemo(() => ({
    pending: mine.filter(t => t.status === 'pending').length,
    inProgress: mine.filter(t => t.status === 'in-progress').length,
    completed: mine.filter(t => t.status === 'completed').length,
    overdue: mine.filter(t => isOverdue(t)).length,
  }), [mine]);

  const updateStatus = useCallback(async (taskId, newStatus) => {
    try {
      const updateData = { status: newStatus, updatedAt: serverTimestamp() };
      if (newStatus === 'completed') updateData.completedAt = serverTimestamp();
      await updateDoc(doc(db, 'Tasks', taskId), updateData);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  }, [fetchTasks]);

  return { myTasks, stats, isLoading, statusFilter, setStatusFilter, unseenTaskIds, updateStatus };
};

export default useMyTasks;
